import { useEffect, useState } from 'react';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth, authPersistenceReady } from '../../firebase';

const OLEMITA_BRAND = {
  name: 'Ole Mita',
  accent: '#ff2ba6',
  accentHover: '#e12192',
  glow: '0 16px 36px -20px rgba(255, 43, 166, 0.55)',
  background:
    'radial-gradient(circle at 18% 22%, rgba(255, 43, 166, 0.12), transparent 32%), radial-gradient(circle at 82% 8%, rgba(59, 130, 246, 0.08), transparent 34%)',
};

const getLoginErrorMessage = (error) => {
  const isOffline = typeof navigator !== 'undefined' && navigator.onLine === false;
  if (isOffline || error?.code === 'auth/network-request-failed') {
    return 'No hay conexión. Recupera Internet para iniciar sesión.';
  }

  switch (error?.code) {
    case 'auth/invalid-credential':
    case 'auth/invalid-email':
    case 'auth/user-not-found':
    case 'auth/wrong-password':
      return 'Correo o contraseña incorrectos.';
    case 'auth/user-disabled':
      return 'Tu usuario está deshabilitado. Consulta con el administrador.';
    case 'auth/too-many-requests':
      return 'Se realizaron demasiados intentos. Espera unos minutos y vuelve a intentar.';
    default:
      return 'No fue posible iniciar sesión. Intenta nuevamente.';
  }
};

const LoginView = ({ externalError = '', onClearExternalError }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (externalError) setError(externalError);
  }, [externalError]);

  const clearErrors = () => {
    setError('');
    onClearExternalError?.();
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    if (loading) return;

    clearErrors();
    setLoading(true);

    try {
      await authPersistenceReady;
      await signInWithEmailAndPassword(auth, email.trim(), password);
      // App.js observa el cambio de Auth y valida el perfil de Firestore.
    } catch (loginError) {
      console.error('login:', loginError);
      setError(getLoginErrorMessage(loginError));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-10"
      style={{ backgroundImage: OLEMITA_BRAND.background }}
    >
      <div className="relative w-full max-w-sm overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#ff2ba6] via-[#ff6bd3] to-[#67c1ff]" />

        {loading && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/85 backdrop-blur-sm">
            <div
              className="h-10 w-10 animate-spin rounded-full border-2 border-blue-500 border-t-transparent"
              aria-label="Validando acceso"
            />
          </div>
        )}

        <div className="px-6 pt-7 pb-6 space-y-4">
          <div className="flex flex-col items-center gap-2">
            <img
              src="https://res.cloudinary.com/dbxgpws0g/image/upload/v1763331474/ChatGPT_Image_16_nov_2025_03_06_01_p.m._c7qlpk.png"
              alt="ValenPOS Logo"
              className="h-16 w-16 rounded-full object-contain shadow-sm bg-white"
              loading="lazy"
              decoding="async"
            />
            <div className="text-center">
              <h1 className="text-2xl font-bold text-slate-900 tracking-tight">ValenPOS</h1>
              <p className="text-xs text-slate-500">Acceso rapido y seguro para Ole Mita</p>
            </div>

            <div className="flex items-center justify-center">
              <div className="flex items-center gap-2 rounded-full bg-white px-3 py-1.5 shadow-sm ring-1 ring-pink-100">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-[#ff2ba6] to-[#ff6bd3] text-[11px] font-bold uppercase text-white shadow-sm">
                  OM
                </span>
                <div className="leading-4">
                  <div className="text-[11px] font-semibold text-slate-800">Ole Mita</div>
                  <div className="text-[10px] text-slate-500">ValenPOS configurado para esta sede</div>
                </div>
              </div>
            </div>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-1">
              <label htmlFor="login-email" className="block text-xs font-semibold text-slate-700">Correo</label>
              <input
                id="login-email"
                type="email"
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                  clearErrors();
                }}
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-100"
                required
                autoFocus
                inputMode="email"
                autoComplete="username"
              />
            </div>

            <div className="space-y-1">
              <label htmlFor="login-password" className="block text-xs font-semibold text-slate-700">Contrasena</label>
              <input
                id="login-password"
                type="password"
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value);
                  clearErrors();
                }}
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-100"
                required
                autoComplete="current-password"
              />
            </div>

            {error && (
              <div role="alert" className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className={`w-full rounded-xl px-6 py-3 text-sm font-semibold text-white transition focus:outline-none focus:ring-2 focus:ring-[#ffcce9] ${
                loading ? 'bg-[#ff9bd6] cursor-not-allowed' : 'bg-[#ff2ba6] hover:bg-[#e12192]'
              }`}
              style={{ boxShadow: OLEMITA_BRAND.glow }}
            >
              {loading ? 'Validando...' : 'Entrar'}
            </button>

            <div className="text-center text-[11px] text-slate-500">
              Sesion gestionada para Ole Mita
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginView;
